import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";
import { QRCodeCanvas } from "qrcode.react";
import { formatCurrency, formatDate } from "../lib/format";
import { Printer, DownloadSimple, WhatsappLogo, ArrowLeft } from "@phosphor-icons/react";

export default function InvoiceView() {
    const { id } = useParams();
    const [inv, setInv] = useState(null);
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { settings } = useSettings();

    useEffect(() => {
        api.get(`/invoices/${id}`)
            .then((r) => setInv(r.data))
            .catch(() => {});
    }, [id]);

    const onPrint = () => {
        window.print();
    };

    const onDownload = async () => {
        const html2canvas = (await import("html2canvas")).default;
        const { jsPDF } = await import("jspdf");
        const node = printRef.current;
        const canvas = await html2canvas(node, { backgroundColor: "#ffffff", scale: 2 });
        const img = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(img, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`${inv.invoice_no}.pdf`);
    };

    const onWhatsApp = () => {
        const text = `Hi ${inv.client_name}, your invoice ${inv.invoice_no} from ${
            settings.company_name || "Proxiq Tech AI"
        } for ${formatCurrency(inv.total)} is ready. Pending due: ${formatCurrency(
            inv.due,
        )}. UPI: ${inv.upi_id || settings.upi_id}.`;
        const phone = (inv.client_mobile || "").replace(/\D/g, "");
        const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
        window.open(url, "_blank");
    };

    if (!inv)
        return (
            <div className="text-slate-400 text-sm" data-testid="invoice-loading">
                Loading invoice…
            </div>
        );

    const upiLink = `upi://pay?pa=${encodeURIComponent(
        inv.upi_id || settings.upi_id || "",
    )}&pn=${encodeURIComponent(settings.company_name || "Proxiq Tech AI")}&am=${
        inv.due > 0 ? inv.due : inv.total
    }&cu=INR&tn=${encodeURIComponent(inv.invoice_no)}`;

    return (
        <div className="space-y-6" data-testid="invoice-view-page">
            <div className="flex items-center justify-between flex-wrap gap-3 no-print">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 text-slate-300 hover:text-white"
                >
                    <ArrowLeft size={16} /> Back
                </button>
                <div className="flex gap-2">
                    <button
                        onClick={onPrint}
                        data-testid="print-invoice"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
                    >
                        <Printer size={16} /> Print
                    </button>
                    <button
                        onClick={onDownload}
                        data-testid="download-invoice"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow text-sm font-medium"
                    >
                        <DownloadSimple size={16} /> Download PDF
                    </button>
                    <button
                        onClick={onWhatsApp}
                        data-testid="whatsapp-invoice"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600/90 hover:bg-emerald-500 text-sm font-medium"
                    >
                        <WhatsappLogo size={16} weight="fill" /> WhatsApp
                    </button>
                </div>
            </div>

            <div
                ref={printRef}
                className="print-page bg-white text-slate-900 mx-auto max-w-3xl p-10 rounded-2xl shadow-2xl"
            >
                <div className="flex items-start justify-between border-b border-slate-200 pb-6">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 grid place-items-center text-white font-display font-bold">
                                {(settings.company_name || "P")[0].toUpperCase()}
                            </div>
                            <div>
                                <div className="font-display text-2xl font-bold tracking-tight">
                                    {(settings.company_name || "PROXIQ TECH AI").toUpperCase()}
                                </div>
                                <div className="text-xs text-slate-500 tracking-[0.25em] font-semibold">
                                    BUSINESS OPERATING SYSTEM
                                </div>
                            </div>
                        </div>
                        <div className="text-xs text-slate-500 mt-3 max-w-xs leading-relaxed">
                            AI-powered orders, billing & coaching platform.
                            {settings.company_email && (
                                <>
                                    <br />Email: {settings.company_email}
                                </>
                            )}
                            {settings.company_phone && (
                                <>
                                    {" · "}Mobile: {settings.company_phone}
                                </>
                            )}
                            {settings.gst_number && (
                                <>
                                    <br />GSTIN: {settings.gst_number}
                                </>
                            )}
                            {settings.company_address && (
                                <>
                                    <br />{settings.company_address}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="font-display text-3xl font-bold text-blue-700">
                            INVOICE
                        </div>
                        <div className="font-mono text-xs text-slate-500 mt-1">
                            {inv.invoice_no}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Issued: {formatDate(inv.issue_date)}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-6">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                            Billed to
                        </div>
                        <div className="font-display text-lg font-semibold mt-1">
                            {inv.client_name}
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{inv.client_address}</div>
                        <div className="text-xs text-slate-600 mt-1">
                            Mobile: {inv.client_mobile}
                        </div>
                        {inv.client_gst && (
                            <div className="text-xs text-slate-600 mt-1">
                                GSTIN: {inv.client_gst}
                            </div>
                        )}
                    </div>
                    <div className="text-right">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                            Pay via UPI
                        </div>
                        <div className="inline-block mt-2 p-2 bg-white border border-slate-200 rounded-lg">
                            <QRCodeCanvas value={upiLink} size={96} level="M" />
                        </div>
                        <div className="text-xs text-slate-600 mt-1 font-mono">
                            {inv.upi_id || settings.upi_id}
                        </div>
                    </div>
                </div>

                <table className="w-full mt-8 text-sm">
                    <thead>
                        <tr className="text-left border-b-2 border-slate-900 text-xs uppercase tracking-widest text-slate-600">
                            <th className="py-2">Description</th>
                            <th className="py-2 text-right">Qty</th>
                            <th className="py-2 text-right">Rate</th>
                            <th className="py-2 text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inv.items.map((it, i) => (
                            <tr key={i} className="border-b border-slate-100">
                                <td className="py-3">{it.description}</td>
                                <td className="py-3 text-right">{it.quantity}</td>
                                <td className="py-3 text-right">{formatCurrency(it.rate)}</td>
                                <td className="py-3 text-right font-semibold">
                                    {formatCurrency(it.amount || it.quantity * it.rate)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="mt-6 flex justify-end">
                    <div className="w-72 text-sm">
                        <div className="flex justify-between py-1">
                            <span className="text-slate-500">Subtotal</span>
                            <span>{formatCurrency(inv.subtotal)}</span>
                        </div>
                        <div className="flex justify-between py-1">
                            <span className="text-slate-500">GST ({inv.gst_percent}%)</span>
                            <span>{formatCurrency(inv.gst_amount)}</span>
                        </div>
                        {inv.discount > 0 && (
                            <div className="flex justify-between py-1">
                                <span className="text-slate-500">Discount</span>
                                <span>−{formatCurrency(inv.discount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between py-2 mt-1 border-t-2 border-slate-900 font-display font-bold text-lg">
                            <span>Total</span>
                            <span>{formatCurrency(inv.total)}</span>
                        </div>
                        <div className="flex justify-between py-1 text-emerald-700">
                            <span>Paid</span>
                            <span>{formatCurrency(inv.paid)}</span>
                        </div>
                        <div className="flex justify-between py-1 font-bold text-rose-700">
                            <span>Balance Due</span>
                            <span>{formatCurrency(inv.due)}</span>
                        </div>
                    </div>
                </div>

                {inv.notes && (
                    <div className="mt-6 text-xs text-slate-600 border-t border-slate-200 pt-4">
                        <span className="font-bold uppercase tracking-widest text-slate-500">
                            Notes:
                        </span>{" "}
                        {inv.notes}
                    </div>
                )}

                <div className="mt-10 grid grid-cols-2 gap-6 items-end border-t border-slate-200 pt-6">
                    <div className="text-xs text-slate-500">
                        Thank you for your business. <br />
                        Terms: Payment due within 7 days of invoice date.
                    </div>
                    <div className="text-right">
                        <div className="font-display text-lg italic text-slate-700">
                            {settings.founder_name || "Aynul Memon"}
                        </div>
                        <div className="text-xs text-slate-500 border-t border-slate-300 pt-1 mt-1">
                            {settings.founder_name || "Aynul Memon"} — Founder of{" "}
                            {settings.company_name || "Proxiq Tech AI"}
                        </div>
                    </div>
                </div>

                <div className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-slate-400">
                    {settings.footer_signature || "Designed & Managed by Proxiq Tech AI"}
                </div>
            </div>
        </div>
    );
}
